import { z } from "zod";
import { CLOAKS, DIARY, DIARY_MEDIA_TYPES, GAME_MODES, LIMITS, SIGILS } from "./domain.js";
import { CASTLE_CODE_ALPHABET, CASTLE_CODE_LENGTH, normaliseCastleCode } from "./codes.js";
import { MISSION_MODES } from "./missions.js";
/**
 * PROTOCOL — the typed messages between clients and the server (Socket.IO).
 *
 * Every client→server request is validated with the zod schemas below, because
 * anything from a phone is untrusted. Every request is acknowledged with a Result.
 */
export const ERROR_CODES = [
    "invalid-request",
    "castle-not-found",
    "castle-full",
    "name-taken",
    "game-started",
    "bad-token",
    "not-host",
    "not-joined",
    /** The action isn't possible in the current phase. */
    "wrong-phase",
    /** The action is possible now, but not for this player or target. */
    "not-allowed",
    "rate-limited",
    "server-error",
];
// ---------- Request schemas (validated on the server) ----------
const castleCode = z
    .string()
    .transform(normaliseCastleCode)
    .pipe(z.string().regex(new RegExp(`^[${CASTLE_CODE_ALPHABET}]{${CASTLE_CODE_LENGTH}}$`), "Castle codes are 4 letters"));
export const playerName = z
    .string()
    .transform((s) => s.replace(/\s+/g, " ").trim())
    .pipe(z
    .string()
    .min(1, "Enter a name")
    .max(LIMITS.nameMaxLength, `Names can be up to ${LIMITS.nameMaxLength} characters`)
    // Letters (any language), numbers, spaces and a few friendly symbols.
    .regex(/^[\p{L}\p{N} .'\-!?&]+$/u, "Letters, numbers and spaces only"));
export const OpenCastleRequest = z.object({
    mode: z.enum(GAME_MODES).default("living-room"),
});
export const AttachScreenRequest = z.object({
    code: castleCode,
    screenToken: z.string().min(10).max(200),
});
export const JoinRequest = z.object({
    code: castleCode,
    name: playerName,
});
export const RejoinRequest = z.object({
    code: castleCode,
    playerId: z.string().min(1).max(64),
    rejoinToken: z.string().min(10).max(200),
});
export const StyleRequest = z.object({
    sigil: z.enum(SIGILS),
    cloak: z.enum(CLOAKS),
});
export const KickRequest = z.object({
    playerId: z.string().min(1).max(64),
});
/** An optional selfie: a small JPEG as a data URL (the phone shrinks it first). Null removes it. */
export const PhotoRequest = z.object({
    image: z
        .string()
        .max(200_000)
        .regex(/^data:image\/jpeg;base64,[A-Za-z0-9+/=]+$/)
        .nullable(),
});
export const PhotosSettingRequest = z.object({ enabled: z.boolean() });
/** The Host's choices before the game, shared so the TV and the Host's phone always agree. */
export const VoiceSignalRequest = z.object({ to: z.string().min(1).max(64), data: z.unknown() });
export const VoiceRequest = z.object({ text: z.string().min(1).max(400) });
export const KeeperDemoVoiceRequest = z.object({ line: z.number().int().min(0).max(20) });
export const LobbySettingsRequest = z.object({ missionMode: z.enum(MISSION_MODES).optional(), familyMode: z.boolean().optional() });
export const TransferHostRequest = z.object({ playerId: z.string().min(1).max(64) });
export const TestGuestsRequest = z.object({
    count: z.number().int().min(1).max(15),
});
export const DiaryUploadRequest = z.object({
    /** The recorder's MIME type, codecs allowed (e.g. "video/webm;codecs=vp8,opus"). */
    contentType: z
        .string()
        .max(100)
        .refine((t) => DIARY_MEDIA_TYPES.includes(t.split(";")[0].trim().toLowerCase()), "That video format isn't supported"),
    bytes: z.number().int().min(1).max(DIARY.maxBytes, "That diary is too long"),
    durationMs: z.number().int().min(0).max(DIARY.maxMs + 2000),
});
//# sourceMappingURL=protocol.js.map